import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  Mail,
  Settings,
  Globe,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  CheckCircle2,
  Clock,
  ExternalLink,
  User,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const EmailSettings = () => {const { t } = useTranslation("comms");
  const [config, setConfig] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/communications/settings/email`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setConfig(data);
    } catch (err) {
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/communications/settings/email`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(config)
      });
      if (!response.ok) throw new Error('Failed to save settings');
      toast.success('Email settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="communications-email">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <Mail className="h-8 w-8 text-violet-600" />{t("emailSettings", "Email Settings")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureResendFor", "Configure Resend for transactional emails")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchSettings} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <span className="text-sm text-muted-foreground">{t("channelStatus", "Channel Status")}</span>
          <Switch
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })} />
          
          <Badge className={config.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
            {config.enabled ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {/* Provider Info */}
      <Card className="card-elevated bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Mail className="h-6 w-6 text-violet-600" />
              </div>
              <div>
                <p className="font-semibold">{t("resend", "Resend")}</p>
                <p className="text-sm text-muted-foreground">{t("emailApiProvider", "Email API Provider")}</p>
              </div>
            </div>
            <a href="https://resend.com/overview" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">
                <ExternalLink className="h-4 w-4 mr-2" />{t("openDashboard", "Open Dashboard")}

              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* API Configuration */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />{t("apiConfiguration", "API Configuration")}

          </CardTitle>
          <CardDescription>{t("configureYourResend", "Configure your Resend API credentials")}

          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("apiKey", "API Key")}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showKey ? 'text' : 'password'}
                  value={config.apiKey || ''}
                  onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="pr-20 font-mono" />
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-8 top-0 h-full"
                  onClick={() => setShowKey(!showKey)}>
                  
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" className="absolute right-0 top-0 h-full">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("generateApiKeys", "Generate API keys from your Resend dashboard")}

            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("defaultReplyto", "Default Reply-To")}</label>
            <Input
              value={config.replyTo || ''}
              onChange={(e) => setConfig({ ...config, replyTo: e.target.value })}
              placeholder="support@yourdomain.com"
              className="font-mono" />
            
          </div>
        </CardContent>
      </Card>

      {/* Domains */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-muted-foreground" />{t("domains", "Domains")}

            </CardTitle>
            <CardDescription>{t("verifiedDomainsFor", "Verified domains for sending emails")}

            </CardDescription>
          </div>
          <Button className="gap-2 bg-violet-600 hover:bg-violet-700">
            <Plus className="h-4 w-4" />{t("addDomain", "Add Domain")}

          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {config.domains?.map((domain) =>
            <div key={domain.id} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium font-mono">{domain.domain}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={domain.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {domain.status === 'verified' ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                        {domain.status}
                      </Badge>
                      {domain.default &&
                    <Badge variant="secondary">{t("default", "Default")}</Badge>
                    }
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {domain.status === 'pending' &&
                <Button variant="outline" size="sm">{t("verifyDns", "Verify DNS")}</Button>
                }
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Senders */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />{t("senderIdentities", "Sender Identities")}

            </CardTitle>
            <CardDescription>{t("configureEmailSender", "Configure email sender names and addresses")}

            </CardDescription>
          </div>
          <Button variant="outline" className="gap-2">
            <Plus className="h-4 w-4" />{t("addSender", "Add Sender")}

          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {config.senders?.map((sender) =>
            <div key={sender.id} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-medium">{sender.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{sender.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={sender.verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                    {sender.verified ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <Clock className="h-3 w-3 mr-1" />}
                    {sender.verified ? 'Verified' : 'Pending'}
                  </Badge>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline">{t("cancel", "Cancel")}</Button>
        <Button className="bg-violet-600 hover:bg-violet-700" onClick={handleSave}>{t("saveChanges", "Save Changes")}</Button>
      </div>
    </div>);

};